import {
  Appointment,
  ScheduleResult,
  UpdateAppointmentDto,
} from "@myapp/contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { appointmentKeys } from "../appointment.keys";
import { scheduleKeys } from "../../../schedule/api/schedule.keys";
import {
  applyEntryPatch,
  ownerEntryPatch,
} from "../../../schedule/model/optimisticEntry";
import { workOrderKeys } from "../../../work-order/api/work-order.keys";
import { appointmentApi } from "../service";
import { auditLogKeys } from "../../../audit-log";
import { rescheduleKeys } from "../../../reschedule/api/reschedule.keys";

export function useUpdateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      dto,
      workOrderId,
    }: {
      id: number;
      dto: UpdateAppointmentDto;
      workOrderId?: string;
    }) => {
      const { data } = await appointmentApi.update(id, dto);
      return { appointment: data as Appointment, workOrderId };
    },

    /**
     * The calendar keeps up with the form.
     *
     * There used to be no optimistic patch here, for a good reason: the block that lived here wrote
     * the pre-`ScheduleEntry` field names into a key no query reads any more, so it painted nothing
     * while looking like it did. This one writes the entry inside the cached `ScheduleResult` — the
     * shape `GET /schedule` actually returns — so saving a new time lands the block immediately
     * instead of leaving it at the old one until the refetch answers.
     *
     * `applyEntryPatch` no-ops on anything it should not touch: an appointment outside the loaded
     * window, and a block drawn from its actuals. With `forFullSeries` only the addressed
     * occurrence is patched; the later ones the server shifts arrive with the invalidation below.
     */
    onMutate: async ({ id, dto }) => {
      const patch = ownerEntryPatch(dto);

      const previousQueries = queryClient.getQueriesData<ScheduleResult>({
        queryKey: scheduleKeys.ranges(),
      });

      // Synchronously, before the await — see `useUpdateScheduleEntry` for why the order matters.
      if (patch) {
        queryClient.setQueriesData<ScheduleResult>(
          { queryKey: scheduleKeys.ranges() },
          (old) => (old ? applyEntryPatch(old, { appointmentId: id }, patch) : old),
        );
      }

      await queryClient.cancelQueries({ queryKey: scheduleKeys.ranges() });

      return { previousQueries };
    },

    // A rejected save — a 409 the user has not confirmed — puts the block back where it was.
    onError: (_err, _vars, context) => {
      for (const [queryKey, data] of context?.previousQueries ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },

    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: rescheduleKeys.count() });
      if (vars?.workOrderId) {
        queryClient.invalidateQueries({ queryKey: auditLogKeys.byWorkOrder(vars.workOrderId) });
      }
    },
  });
}
